import React, { useEffect, useState } from 'react';
import { format, parse } from 'date-fns';
import { MOLD_RELEASE } from '../../core/constants/moldConstants';
import { selectCustomerData } from 'Core/redux/customer/selectors';
import { useSelector } from 'react-redux';
import { useTransformOptions } from '../../core/hooks';
import { SimpleFileUpload, DocumentViewerModal } from '../../core/components';
import { PartService } from 'Service/api/part-service';
import { Eye } from 'react-feather';
import WebcamFileUpload from '../../core/components/webcamfileupload/webcam-file-upload';
import { Modal, Button } from 'react-bootstrap';

const normalizePartsForSelect = parts => {
  if (!Array.isArray(parts)) {
    return [];
  }

  return parts
    .filter(part => !!part)
    .map(part => {
      const partId =
        part.partId ??
        part.PartId ??
        part.partID ??
        part.id ??
        part.value ??
        null;
      const partNumber =
        part.partNumber ??
        part.PartNumber ??
        part.label ??
        part.partname ??
        part.PartName ??
        '';

      return {
        value: partId,
        label: partNumber,
        partId,
        partNumber,
      };
    });
};

const initData = {
  toolId: '',
  customerName: '',
  customerEmail: '',
  customerPhone: '',
  customerContact: '',
  moldNumber: '',
  moldDescription: '',
  partID: 0,
  outsourcing: 'false',
  outsourcingCompany: '',
  outsourcingCustomerContact: '',
  outsourcingCustomerEmail: '',
  outsourcingCustomerPhone: '',
  open1: 'true',
  open2: 'false',
  open3: 'true',
  open4: 'false',
  picture1: '',
  picture2: '',
  picture3: '',
  picture4: '',
  loaded1: 'true',
  loaded2: 'false',
  boL_Signed_Copy: '',
  packingSlip_Signed_Copy: '',
  release_Approver: '',
  release_Date: '',
  submitted_By: '',
  parts: [], // Parts list for multi-select
};

const uploadFieldMap = {
  OPEN_ONE: 'Open1',
  OPEN_TWO: 'Open2',
  OPEN_THREE: 'Open3',
  OPEN_FOUR: 'Open4',
  PICTURE_ONE: 'Picture1',
  PICTURE_TWO: 'Picture2',
  PICTURE_THREE: 'Picture3',
  PICTURE_FOUR: 'Picture4',
  LOADED_ONE: 'Loaded1',
  LOADED_TWO: 'Loaded2',
  BOL_SIGNED_COPY: 'BOL_Signed_Copy',
  PACKING_SLIP_SIGNED_COPY: 'PackingSlip_Signed_Copy',
};

const MoldReleaseAddEditForm = ({ data, onChanges, onFileUpload }) => {
  // Check if this is a released mold (read-only mode)
  const isReleased = data?.isReleased || false;
  const [moldReceivingData, setMoldReceivingData] = useState(
    data?.moldReceivingData ? data.moldReceivingData : {},
  );
  const [formData, setFormData] = useState(
    data?.moldReleaseData ? data?.moldReleaseData : initData,
  );
  const [partsList, setPartsList] = useState([]);
  // Add draft ID state to track the draft record ID
  const [draftId, setDraftId] = useState(0);
  // Add state to track the actual mold receiving ID from draft response
  const [draftMoldInfoID, setDraftMoldInfoID] = useState(0);
  // Add state to track image segments from API response
  const [imageSegments, setImageSegments] = useState([]);
  const [showPartsModal, setShowPartsModal] = useState(false);
  const [showDocumentViewer, setShowDocumentViewer] = useState(false);
  const [documentViewerUrl, setDocumentViewerUrl] = useState('');
  const [documentViewerName, setDocumentViewerName] = useState('');

  // Function to process image segments and group them by button label
  const processImageSegments = (images) => {
    if (!images || !Array.isArray(images)) return {};
    
    const segmentsByButton = {};
    
    images.forEach(image => {
      const buttonLabel = image.record; // e.g., "Open 1", "Open 2", etc.
      if (!segmentsByButton[buttonLabel]) {
        segmentsByButton[buttonLabel] = [];
      }
      segmentsByButton[buttonLabel].push(image);
    });
    
    return segmentsByButton;
  };

  // Function to get segments for a specific button label
  const getSegmentsForButton = (buttonLabel) => {
    return imageSegments.filter(segment => segment.record === buttonLabel);
  };

  // Function to check if a button should show "Manage Images"
  const shouldShowManageImages = (buttonLabel) => {
    const segments = getSegmentsForButton(buttonLabel);
    return segments.length > 0;
  };

  // Callback function to handle draft ID updates from webcam modals
  const handleDraftIdUpdate = (newDraftId, newMoldInfoID) => {
    if (newDraftId && newDraftId > 0) {
      setDraftId(newDraftId);
    }
    
    if (newMoldInfoID && newMoldInfoID > 0) {
      setDraftMoldInfoID(newMoldInfoID);
      
      // Also update the formData to include the correct moldInfoID
      setFormData(prevData => ({
        ...prevData,
        moldInfoID: newMoldInfoID,
        draftId: newDraftId
      }));
      
      // Notify parent component of the updated draft IDs
      onChanges({ 
        ...formData, 
        moldInfoID: newMoldInfoID, 
        draftId: newDraftId,
        draftMoldInfoID: newMoldInfoID
      });
    }
  };

  useEffect(() => {
    if (moldReceivingData?.moldInfoID) {
      setFormData(prevData => {
                 const updatedData = {
           ...prevData,
           moldInfoID: moldReceivingData.moldInfoID,
           customerInfoID: moldReceivingData.customerId || 0,
         };
        
        // Notify parent of the updated data
        onChanges({
          ...updatedData,
          moldInfoID: moldReceivingData.moldInfoID,
          draftId: draftId || 0,
          draftMoldInfoID: draftMoldInfoID || 0
        });
        
        return updatedData;
      });
    }
  }, [moldReceivingData]);

  // Initialize formData and moldReceivingData with the data prop when component mounts
  useEffect(() => {
    if (data?.moldReceivingData) {
      setMoldReceivingData(data.moldReceivingData);
      
      // Set the moldInfoID in formData from moldReceivingData immediately
      setFormData(prevData => {
                 const updatedData = {
           ...prevData,
           moldInfoID: data.moldReceivingData.moldInfoID || 0,
           customerInfoID: data.moldReceivingData.customerId || 0
         };
        
                 // Immediately notify parent of the updated data with moldInfoID
         onChanges({
           ...updatedData,
           moldInfoID: data.moldReceivingData.moldInfoID || 0,
           customerInfoID: data.moldReceivingData.customerId || 0,
           draftId: 0,
           draftMoldInfoID: 0
         });
        
        return updatedData;
      });
    }
    
    if (data?.moldReleaseData) {
      // Normalize parts from API response
      const normalizedParts = normalizePartsForSelect(
        data.moldReleaseData.parts || data.moldReleaseData.moldParts || data.moldReleaseData.selectedParts
      ) || [];
      
      // Set primary partID from parts if not already set
      let primaryPartID = data.moldReleaseData.partID || 0;
      if (!primaryPartID && normalizedParts.length > 0) {
        const firstWithId = normalizedParts.find(option => option.value);
        primaryPartID = firstWithId ? firstWithId.value : 0;
      }
      
      setFormData(prevData => ({
        ...prevData,
        ...data.moldReleaseData,
        parts: normalizedParts,
        partID: primaryPartID,
        // Ensure outsourcing defaults to 'false' if not provided or null/undefined
        outsourcing: data.moldReleaseData.outsourcing || 'false'
      }));
      
      // Process image segments if they exist in the response
      if (data.moldReleaseData.images && Array.isArray(data.moldReleaseData.images)) {
        setImageSegments(data.moldReleaseData.images);
        processImageSegments(data.moldReleaseData.images);
      }
    }
    
    // Also check moldReceivingData for parts
    if (data?.moldReceivingData?.parts) {
      const normalizedParts = normalizePartsForSelect(data.moldReceivingData.parts) || [];
      setFormData(prevData => ({
        ...prevData,
        parts: normalizedParts.length > 0 ? normalizedParts : prevData.parts,
        partID: prevData.partID || (normalizedParts.find(option => option.value)?.value || 0)
      }));
    }
  }, [data]);



  useEffect(() => {
    const fetchParts = async () => {
      try {
        const response = await PartService.getAllPartsList();
        if (response?.response) {
          setPartsList(response.response);
        }
      } catch (error) {
        // Error fetching parts list
      }
    };

    fetchParts();
  }, []);

  const [isOutsourcing, setIsOutsourcing] = useState(false);
  const customers = useSelector(selectCustomerData);

  // Sync isOutsourcing state with formData.outsourcing value
  useEffect(() => {
    if (formData.outsourcing === 'true') {
      setIsOutsourcing(true);
    } else if (formData.outsourcing === 'false') {
      setIsOutsourcing(false);
    }
  }, [formData.outsourcing]);

  const customerOptions = useTransformOptions(
    customers,
    'id',
    'customerName',
    '',
    'Select Customer',
  );

  const updateFormField = (fieldName, value) => {
    setFormData(prevData => {
      const updatedData = {
        ...prevData,
        [fieldName]: value,
      };
      
             // Always include the current moldInfoID and draft information when notifying parent
       onChanges({ 
         ...updatedData,
         // Preserve the moldInfoID (either original or from draft)
         moldInfoID: updatedData.moldInfoID || 0,
         // Preserve the customerInfoID
         customerInfoID: updatedData.customerInfoID || 0,
         // Include draft information if available
         draftId: draftId || 0,
         draftMoldInfoID: draftMoldInfoID || 0
       });
      
      return updatedData;
    });
  };

  const handleFileUpload = eOrFiles => {
    // If called from WebcamFileUpload, eOrFiles will be an array of files
    if (Array.isArray(eOrFiles)) {
      const paths = eOrFiles.map(f => f.path).join(',');
      const fieldName = eOrFiles[0]?.id;
      const mappedField = uploadFieldMap[fieldName] || fieldName;
      if (mappedField) {
        updateFormField(mappedField, paths);
      }
      return;
    }
    // Otherwise, handle as a file input event
    const e = eOrFiles;
    if (e && e.target && e.target.files) {
      const file = e.target.files[0];
      const fieldName = e.target.id;
      const mappedField = uploadFieldMap[fieldName] || fieldName;
      if (file && mappedField) {
        onFileUpload(fieldName, file); // parent needs original for upload
        // Optionally clear or set as needed
      }
    }
  };

  const getDocumentPath = documentPath => {
    let fileName = '';
    if (documentPath != undefined) {
      const encodedFileName = documentPath?.split('/').pop();
      const decoded = decodeURIComponent(encodedFileName);
      const lastUnderscore = decoded.lastIndexOf('_');
      const extensionIndex = decoded.lastIndexOf('.');

      if (lastUnderscore !== -1 && extensionIndex !== -1 && lastUnderscore < extensionIndex) {
        fileName = decoded?.slice(0, lastUnderscore) + decoded?.slice(extensionIndex);
      } else {
        fileName = decoded;
      }
    }

    return documentPath ? { fileUrl: documentPath, fileName: fileName } : '';
  };

  const parseDate = dateStr => {
    if (!dateStr) return '';
    const parsedDate = parse(dateStr, 'M/d/yyyy hh:mm:ss a', new Date());
    return format(parsedDate, 'yyyy-MM-dd'); // Ensures YYYY-MM-DD format
  };

  return (
    <div className="card table-list-card">
      <div className="card-body">
        <div className="m-2">
          {/* Customer Information*/}
          <h5 className="header-five">Customer Information</h5>
          <div className="row m-2">
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks ">
                <label>
                  ToolID <span className="text-danger">*</span>
                </label>
                <input
                  placeholder=""
                  type="text"
                  value={moldReceivingData.toolID}
                  disabled={true}
                  className="form-control"
                />
              </div>
            </div>
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Customer Name <span className="text-danger">*</span>
                </label>

                <select
                  value={moldReceivingData.customerInfoID}
                  className="form-control"
                  required
                  disabled={true}
                >
                  {customerOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Customer Email <span className="text-danger">*</span>
                </label>
                <input
                  type="email"
                  value={moldReceivingData.customerEmail}
                  disabled={true}
                  className="form-control"
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Customer Phone <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={moldReceivingData.customerPhone}
                  disabled={true}
                  className="form-control"
                  placeholder=""
                />
              </div>
            </div>
          </div>

          <div className="row m-2">
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Customer Contact <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={moldReceivingData.customerContact}
                  disabled={true}
                  className="form-control"
                  placeholder=""
                />
              </div>
            </div>
          </div>

          {/*Mold Information*/}
          <h5 className="header-five">Mold Information</h5>
          <div className="row m-2">
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Mold Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={moldReceivingData.moldNumber}
                  disabled={true}
                  className="form-control"
                  placeholder=""
                />
              </div>
            </div>

            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Mold Description
                </label>
                <input
                  type="text"
                  value={moldReceivingData.moldDescription}
                  disabled={true}
                  className="form-control"
                  placeholder=""
                />
              </div>
            </div>

            {/* <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Part Name <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={formData.partName}
                  disabled={true}
                  className="form-control"
                  placeholder=""
                />
              </div>
            </div> */}
            {/* <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Part Number <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  value={formData.partNumber}
                  disabled={true}
                  className="form-control"
                  placeholder=""
                />
              </div>
            </div> */}

            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>
                  Part
                </label>
                <div className="d-flex align-items-start gap-2">
                  <div className="flex-grow-1">
                    <input
                      type="text"
                      className="form-control"
                      value={
                        (formData.parts || []).length > 0
                          ? `${(formData.parts || []).length} part${(formData.parts || []).length > 1 ? 's' : ''} selected`
                          : 'No parts selected'
                      }
                      readOnly
                      disabled
                      style={{ cursor: 'not-allowed', backgroundColor: '#e9ecef' }}
                    />
                  </div>
                  <Button
                    variant="outline-secondary"
                    className="d-flex align-items-center justify-content-center mt-1"
                    title="View all parts"
                    type="button"
                    onClick={() => setShowPartsModal(true)}
                    disabled={!(formData.parts || []).length}
                  >
                    <Eye size={16} />
                  </Button>
                </div>
                <div className="invalid-feedback">Please provide a valid Part ID</div>
              </div>
            </div>
          </div>

          {/* OutSourcing*/}
          <h5 className="header-five">Outsourcing</h5>
          <div className="row m-2">
            <div className="col-lg-3 col-md-3 col-sm-12">
              <div className="input-blocks">
                <label>Outsourcing</label>
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic radio toggle button group"
                >
                  <input
                    type="radio"
                    className="btn-check"
                    name="btnradioOutSourc"
                    id="btnradioYes"
                    checked={formData.outsourcing === 'true'}
                    value="Yes"
                    disabled={isReleased}
                    onChange={e => {
                      updateFormField('outsourcing', 'true');
                      setIsOutsourcing(true);
                    }}
                  />
                  <label className="btn btn-outline-primary" htmlFor="btnradioYes">
                    Yes
                  </label>
                  <input
                    type="radio"
                    className="btn-check"
                    name="btnradioOutSourc"
                    id="btnradioNo"
                    checked={formData.outsourcing === 'false'}
                    value="No"
                    disabled={isReleased}
                    onChange={e => {
                      updateFormField('outsourcing', 'false');
                      setIsOutsourcing(false);
                    }}
                  />
                  <label className="btn btn-outline-primary" htmlFor="btnradioNo">
                    No
                  </label>
                </div>
              </div>
            </div>
          </div>
          <div className="row m-2">
            {isOutsourcing && (
              <>
                <div className="col-lg-3 col-md-3 col-sm-12">
                  <div className="input-blocks">
                    <label>Outsourcing Company</label>
                    <input
                      type="text"
                      value={formData.outsourcingCompany}
                      onChange={e => updateFormField('outsourcingCompany', e.target.value)}
                      className="form-control"
                      placeholder=""
                      disabled={isReleased}
                    />
                  </div>
                </div>

                <div className="col-lg-3 col-md-3 col-sm-12">
                  <div className="input-blocks">
                    <label>Outsourcing Customer Contact</label>
                    <input
                      type="text"
                      value={formData.outsourcingCustomerContact}
                      onChange={e => updateFormField('outsourcingCustomerContact', e.target.value)}
                      className="form-control"
                      placeholder=""
                      disabled={isReleased}
                    />
                  </div>
                </div>

                <div className="col-lg-3 col-md-3 col-sm-12">
                  <div className="input-blocks">
                    <label>Outsourcing Customer Email</label>
                    <input
                      type="text"
                      value={formData.outsourcingCustomerEmail}
                      onChange={e => updateFormField('outsourcingCustomerEmail', e.target.value)}
                      className="form-control"
                      disabled={isReleased}
                    />
                  </div>
                </div>

                <div className="col-lg-3 col-md-3 col-sm-12">
                  <div className="input-blocks">
                    <label>Outsourcing Customer Phone</label>
                    <input
                      type="text"
                      value={formData.outsourcingCustomerPhone}
                      onChange={e => updateFormField('outsourcingCustomerPhone', e.target.value)}
                      className="form-control"
                      disabled={isReleased}
                    />
                  </div>
                </div>
              </>
            )}
          </div>

          {/* Pictures*/}
          <h5 className="header-five">Pictures</h5>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Open 1</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.OPEN_ONE}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.open1)?.fileName}
                    filePath={getDocumentPath(formData.open1)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Open 1"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Open 1")}
                    showManageImages={shouldShowManageImages("Open 1")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Open 2</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.OPEN_TWO}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.open2)?.fileName}
                    filePath={getDocumentPath(formData.open2)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Open 2"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Open 2")}
                    showManageImages={shouldShowManageImages("Open 2")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
          </div>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Open 3</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.OPEN_THREE}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.open3)?.fileName}
                    filePath={getDocumentPath(formData.open3)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Open 3"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Open 3")}
                    showManageImages={shouldShowManageImages("Open 3")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Open 4</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.OPEN_FOUR}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.open4)?.fileName}
                    filePath={getDocumentPath(formData.open4)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Open 4"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Open 4")}
                    showManageImages={shouldShowManageImages("Open 4")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
          </div>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Picture 1</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.PICTURE_ONE}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.picture1)?.fileName}
                    filePath={getDocumentPath(formData.picture1)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Picture 1"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Picture 1")}
                    showManageImages={shouldShowManageImages("Picture 1")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Picture 2</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.PICTURE_TWO}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.picture2)?.fileName}
                    filePath={getDocumentPath(formData.picture2)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Picture 2"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Picture 2")}
                    showManageImages={shouldShowManageImages("Picture 2")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
          </div>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Picture 3</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.PICTURE_THREE}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.picture3)?.fileName}
                    filePath={getDocumentPath(formData.picture3)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Picture 3"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Picture 3")}
                    showManageImages={shouldShowManageImages("Picture 3")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Picture 4</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.PICTURE_FOUR}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.picture4)?.fileName}
                    filePath={getDocumentPath(formData.picture4)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Picture 4"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Picture 4")}
                    showManageImages={shouldShowManageImages("Picture 4")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
          </div>

          {/* Loaded*/}
          <h5 className="header-five">Loaded</h5>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Loaded 1</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.LOADED_ONE}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.loaded1)?.fileName}
                    filePath={getDocumentPath(formData.loaded1)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Loaded 1"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Loaded 1")}
                    showManageImages={shouldShowManageImages("Loaded 1")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Loaded 2</label>
                                                   <WebcamFileUpload
                    id={MOLD_RELEASE.LOADED_TWO}
                    onChange={handleFileUpload}
                    value={getDocumentPath(formData.loaded2)?.fileName}
                    filePath={getDocumentPath(formData.loaded2)?.fileUrl}
                    context="release"
                    maintenanceData={{ ...formData, draftId, customerInfoID: moldReceivingData.customerId || 0 }}
                    buttonLabel="Loaded 2"
                    moldInfoID={formData.moldInfoID}
                    onDraftIdUpdate={handleDraftIdUpdate}
                    documents={getSegmentsForButton("Loaded 2")}
                    showManageImages={shouldShowManageImages("Loaded 2")}
                    isDisabled={isReleased}
                  />
              </div>
            </div>
          </div>

          {/* Documentation*/}
          <h5 className="header-five">Documentation</h5>
          <div className="row m-2">
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>BOL Signed Copy</label>
                <SimpleFileUpload
                  id={MOLD_RELEASE.BOL_SIGNED_COPY}
                  onChange={handleFileUpload}
                  value={getDocumentPath(formData.boL_Signed_Copy)?.fileName}
                  filePath={getDocumentPath(formData.boL_Signed_Copy)?.fileUrl}
                  isDisabled={isReleased}
                  onViewClick={(filePath, fileName) => {
                    setDocumentViewerUrl(filePath);
                    setDocumentViewerName(fileName);
                    setShowDocumentViewer(true);
                  }}
                />
              </div>
            </div>
            <div className="col-lg-6 col-md-6 col-sm-12">
              <div className="input-blocks">
                <label>Packing Slip Signed Copy</label>
                <SimpleFileUpload
                  id={MOLD_RELEASE.PACKING_SLIP_SIGNED_COPY}
                  onChange={handleFileUpload}
                  value={getDocumentPath(formData.packingSlip_Signed_Copy)?.fileName}
                  filePath={getDocumentPath(formData.packingSlip_Signed_Copy)?.fileUrl}
                  isDisabled={isReleased}
                  onViewClick={(filePath, fileName) => {
                    setDocumentViewerUrl(filePath);
                    setDocumentViewerName(fileName);
                    setShowDocumentViewer(true);
                  }}
                />
              </div>
            </div>
          </div>

          {/*Loaded Name*/}
          <h5 className="header-five">Loaded Name</h5>
          <div className="row m-2">
            <div className="col-lg-8 col-md-8 col-sm-12">
              <div className="input-blocks">
                <label>Release Approver</label>
                <div className="d-flex">
                  <input
                    type="email"
                    value={formData.release_Approver}
                    onChange={e => updateFormField('release_Approver', e.target.value)}
                    className="form-control"
                    placeholder="Email"
                    disabled={isReleased}
                  />
                  <input
                    type="date"
                    value={formData.release_Date}
                    onChange={e => updateFormField('release_Date', e.target.value)}
                    className="form-control ms-2"
                    disabled={isReleased}
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="row m-2">
            <div className="col-lg-4 col-md-4 col-sm-12">
              <div className="input-blocks">
                <label>Submitted By</label>
                <input
                  type="email"
                  value={formData.submittedBy}
                  onChange={e => updateFormField('submittedBy', e.target.value)}
                  className="form-control"
                  placeholder="Email "
                  disabled={isReleased}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Parts View Modal */}
      <Modal show={showPartsModal} onHide={() => setShowPartsModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Selected Parts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {(formData.parts || []).length === 0 ? (
            <p className="mb-0 text-muted">No parts selected.</p>
          ) : (
            <ul className="list-group list-group-flush">
              {(formData.parts || []).map((part, index) => (
                <li
                  key={`${part.value ?? part.partId ?? part.partNumber ?? index}`}
                  className="list-group-item"
                >
                  <span className="fw-medium">{part.partNumber || part.label || 'N/A'}</span>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
      </Modal>

      {/* Document Viewer Modal */}
      <DocumentViewerModal
        show={showDocumentViewer}
        onHide={() => setShowDocumentViewer(false)}
        documentUrl={documentViewerUrl}
        documentName={documentViewerName}
      />
    </div>
  );
};

export default MoldReleaseAddEditForm;
